/**
 * Profil-Popout (Klick auf Avatar/Name/Erwähnung): Banner, animierter Avatar, Status, eigener Status,
 * Namen, Pronomen, Bio; im Server-Kontext Spitzname, Rollen (mit Recht zuweisbar), Mitglied seit;
 * gemeinsame Server; „Nachricht senden" öffnet die DM (POST /dms, idempotent).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Permissions, hasPermission, type MemberDto, type RoleDto } from '@lagerfeuer/shared';
import { MessageCircle, Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Popover, type Anchor } from '../../components/Popover';
import { errorMessage } from '../../lib/api';
import { dmsApi, membersApi, usersApi, type UserProfileDto } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { paths } from '../../lib/routes';
import { appState, useAppStore } from '../../store/app';
import { highestRolePosition, serverPermissions } from '../../store/selectors';
import { toast } from '../../store/ui';
import { ProfileCard } from './ProfileCard';
import './profile.css';

export interface UserPopoutProps {
  userId: string;
  serverId?: string | null;
  anchor: Anchor;
  onClose: () => void;
}

const fmtDay = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });
const roleColor = (r: RoleDto) => (r.color ? `#${r.color.toString(16).padStart(6, '0')}` : undefined);

export function UserPopout({ userId, serverId, anchor, onClose }: UserPopoutProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const meId = useAppStore((s) => s.me?.id);
  const isMe = meId === userId;
  const storeUser = useAppStore((s) => (s.me?.id === userId ? s.me : s.users[userId]));
  const storeMember = useAppStore((s) => (serverId ? s.members[serverId]?.[userId] ?? (s.servers[serverId]?.me.user.id === userId ? s.servers[serverId]?.me : undefined) : undefined));
  const presence = useAppStore((s) => s.presences[userId]);
  const myStatus = useAppStore((s) => (isMe ? s.me?.status : undefined));
  const myCustom = useAppStore((s) => (isMe ? s.me?.customStatus : undefined));
  const roles = useAppStore((s) => (serverId ? s.roles[serverId] : undefined));
  const servers = useAppStore((s) => s.servers);
  const profileKey = qk.profile(userId, serverId ?? '');
  const profile = useQuery({ queryKey: profileKey, queryFn: () => usersApi.profile(userId, serverId ?? undefined), staleTime: 60_000 });
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);

  // Rechte zum Rollen-Zuweisen (MANAGE_ROLES + Hierarchie; Owner/Admin dürfen alles).
  // Ergebnis: höchste verwaltbare Rollenposition (exklusiv) bzw. -1 ohne Recht.
  const manageBelow = useAppStore((s) => {
    const server = serverId ? s.servers[serverId] : undefined;
    if (!serverId || !server) return -1;
    const perms = serverPermissions(s, serverId);
    if (server.ownerId === s.me?.id || hasPermission(perms, Permissions.ADMINISTRATOR)) return Number.POSITIVE_INFINITY;
    if (!hasPermission(perms, Permissions.MANAGE_ROLES)) return -1;
    return highestRolePosition(s.roles[serverId] ?? [], server.me);
  });
  const canManage = { can: manageBelow >= 0, maxPos: manageBelow };

  const user = profile.data?.user ?? storeUser;
  const member: MemberDto | null | undefined = profile.data?.member ?? storeMember;
  const status = isMe ? (myStatus ?? 'ONLINE') : (presence?.status === 'INVISIBLE' ? 'OFFLINE' : presence?.status) ?? 'OFFLINE';
  const customStatus = isMe ? myCustom : presence?.customStatus;

  if (!user) {
    return (
      <Popover anchor={anchor} onClose={onClose} placement="right-start" className="lf-user-popout" testId="user-popout" label="Profil">
        <div className="empty" data-testid="user-popout-loading">
          {profile.isError ? <span className="error-text">{errorMessage(profile.error)}</span> : <div className="spinner" aria-label="Lädt" />}
        </div>
      </Popover>
    );
  }

  const memberRoles = (roles ?? []).filter((r) => !r.isEveryone && member?.roleIds.includes(r.id)).sort((a, b) => b.position - a.position);
  const assignable = (roles ?? []).filter((r) => !r.isEveryone && !r.managed && r.position < canManage.maxPos && !member?.roleIds.includes(r.id)).sort((a, b) => b.position - a.position);
  const mutual = (profile.data?.mutualServerIds ?? []).map((id) => servers[id]).filter((s): s is NonNullable<typeof s> => !!s);

  const setRoleIds = (fn: (ids: string[]) => string[]) => {
    qc.setQueryData<UserProfileDto>(profileKey, (old) => (old?.member ? { ...old, member: { ...old.member, roleIds: fn(old.member.roleIds) } } : old));
    if (serverId)
      appState().update((d) => {
        const m = d.members[serverId]?.[userId];
        if (m) m.roleIds = fn(m.roleIds);
        const s = d.servers[serverId];
        if (s && s.me.user.id === userId) s.me.roleIds = fn(s.me.roleIds);
      });
  };
  const addRole = async (r: RoleDto) => {
    if (!serverId) return;
    setBusy(true);
    try {
      await membersApi.addRole(serverId, userId, r.id);
      setRoleIds((ids) => [...new Set([...ids, r.id])]);
      setPicking(false);
    } catch (e) {
      toast.error('Rolle konnte nicht zugewiesen werden', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const removeRole = async (r: RoleDto) => {
    if (!serverId) return;
    setBusy(true);
    try {
      await membersApi.removeRole(serverId, userId, r.id);
      setRoleIds((ids) => ids.filter((x) => x !== r.id));
    } catch (e) {
      toast.error('Rolle konnte nicht entfernt werden', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const openDm = async () => {
    setBusy(true);
    try {
      const existing = profile.data?.mutualDmChannelId;
      let channelId = existing ?? null;
      if (!channelId) {
        const ch = await dmsApi.open([userId]);
        appState().update((d) => {
          d.channels[ch.id] = ch;
          if (!d.dmIds.includes(ch.id)) d.dmIds.unshift(ch.id);
        });
        channelId = ch.id;
      }
      onClose();
      void navigate(paths.dm(channelId));
    } catch (e) {
      toast.error('Direktnachricht nicht möglich', errorMessage(e));
      setBusy(false);
    }
  };

  const actions = isMe ? (
    <button
      type="button"
      className="btn btn-sm"
      onClick={() => {
        onClose();
        void navigate(paths.settings('profil'));
      }}
      data-testid="popout-edit-profile"
    >
      <Pencil size={14} aria-hidden="true" /> Profil bearbeiten
    </button>
  ) : !user.isShadow ? (
    <button type="button" className="btn btn-primary btn-sm" onClick={() => void openDm()} disabled={busy} data-testid="popout-message">
      <MessageCircle size={14} aria-hidden="true" /> Nachricht senden
    </button>
  ) : null;

  return (
    <Popover anchor={anchor} onClose={onClose} placement="right-start" className="lf-user-popout" testId="user-popout" label={`Profil von ${user.displayName || user.username}`}>
      <ProfileCard
        user={user}
        nickname={member?.nickname}
        avatarUrl={member?.serverAvatarUrl}
        bannerUrl={member?.serverBannerUrl}
        bio={member?.serverBio}
        status={status}
        customStatus={customStatus}
        serverId={serverId}
        actions={actions}
        testId="popout-card"
      >
        {serverId && member && (
          <>
            <div className="lf-profile-section">
              <div className="lf-profile-section-title">Mitglied seit</div>
              <div className="small" data-testid="popout-member-since">
                {fmtDay(member.joinedAt)}
                <span className="faint"> · Konto erstellt {fmtDay(user.createdAt)}</span>
              </div>
            </div>
            {(memberRoles.length > 0 || canManage.can) && (
              <div className="lf-profile-section">
                <div className="lf-profile-section-title">{memberRoles.length === 1 ? 'Rolle' : 'Rollen'}</div>
                <div className="lf-role-chips" data-testid="popout-roles">
                  {memberRoles.map((r) => (
                    <span key={r.id} className="lf-role-chip" data-testid="popout-role">
                      <span className="lf-role-chip-dot" style={{ background: roleColor(r) }} aria-hidden="true" />
                      {r.name}
                      {canManage.can && !r.managed && r.position < canManage.maxPos && (
                        <button type="button" aria-label={`Rolle ${r.name} entfernen`} onClick={() => void removeRole(r)} disabled={busy} data-testid="popout-role-remove">
                          <X size={12} />
                        </button>
                      )}
                    </span>
                  ))}
                  {canManage.can && assignable.length > 0 && (
                    <button type="button" className="lf-role-chip" onClick={() => setPicking((v) => !v)} aria-expanded={picking} aria-label="Rolle hinzufügen" data-testid="popout-add-role">
                      <Plus size={12} aria-hidden="true" />
                    </button>
                  )}
                </div>
                {picking && (
                  <div className="lf-role-picker" role="listbox" aria-label="Rolle wählen" data-testid="popout-role-picker">
                    {assignable.map((r) => (
                      <button key={r.id} type="button" role="option" aria-selected={false} onClick={() => void addRole(r)} disabled={busy} data-testid={`popout-role-option-${r.id}`}>
                        <span className="lf-role-chip-dot" style={{ background: roleColor(r) }} aria-hidden="true" />
                        {r.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {!isMe && mutual.length > 0 && (
          <div className="lf-profile-section">
            <div className="lf-profile-section-title">Gemeinsame Server – {mutual.length}</div>
            <div className="lf-mutual-servers" data-testid="popout-mutual-servers">
              {mutual.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="lf-mutual-server"
                  onClick={() => {
                    onClose();
                    void navigate(paths.server(s.id));
                  }}
                  data-testid="popout-mutual-server"
                >
                  {s.iconUrl ? <img src={s.iconUrl} alt="" /> : <span className="lf-mutual-initial">{s.name.slice(0, 2).toUpperCase()}</span>}
                  <span className="truncate">{s.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </ProfileCard>
    </Popover>
  );
}
